import React, { useState, useMemo } from 'react';
import { Col, Badge, Flex } from 'antd';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { RawHtml } from '../../../../../common/index.jsx';
import NewsModal from './NewsModal/NewsModal.jsx';
import dayjs from 'dayjs';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import useTranslateMethod from '../../../../../../utils/helpers/useTranslateMethod.jsx';
import { useTranslation } from 'react-i18next';
import {
  DATETIME_FORMAT_DATETIME,
  formattedFullName,
  isUserAndItsMorphIdentical,
} from '../../../../../../utils/helpers/CommonHelper.jsx';
import usePermission from '../../../../../../hooks/usePermission.jsx';
import { useSelector } from 'react-redux';
import useIsUserAdmin from '../../../../../../hooks/useIsUserAdmin.jsx';
import ControlsActions from '../../../../../common/controls/ControlsActions/ControlsActions.jsx';
import { EditOutlined } from '@ant-design/icons';
import { DEFAULT_IMAGE } from '../../../../../../utils/constants/common_constants.js';
import { getFilenameFromPath } from '../../../../../../utils/helpers/CommonHelper.jsx';
import { NewsSignature } from '../../../../../../utils/helpers/UtilityComponents.jsx';

const NewsItem = ({ item, redirectToNewsEdit }) => {
  const [modalVisible, displayModal] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const isAuthor = useMemo(
    () => isUserAndItsMorphIdentical(user, item.person),
    [item.person, user],
  );
  const isAdmin = useIsUserAdmin();
  const canUpdate = usePermission('news.update');

  const title = useTranslateMethod(item.title_sk, item.title_en);
  const content = useTranslateMethod(item.content_sk, item.content_en);
  const slug = useTranslateMethod(item.slug_sk, item.slug_en);
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const baseNewsRoute = isEnglish ? routes.newsDetailEnglish : routes.newsDetail;

  return (
    <Col
      lg={{ span: 12 }}
      sm={{ span: 24 }}
      xs={{ span: 24 }}
      className="hp-news-item-container"
    >
      <Badge.Ribbon
        text="Top"
        color={'#191970'}
        style={{
          display: dayjs(item.top_to, 'YYYY-MM-DD HH:mm:ss').isAfter(dayjs())
            ? 'block'
            : 'none',
        }}
      >
        <div
          className={`main-shadow xs-p-5 news-wrapper`}
          style={{ paddingTop: '30px' }}
        >
          <div
            onClick={() => displayModal(true)}
            className="news-content-wrapper"
          >
            <div className="title-wrapper">
              <Flex justify={'space-between'} align={'flex-start'}>
                <Link to={`${baseNewsRoute}/${slug}`}>
                  <div className="second-title">{title}</div>
                </Link>
                {isAdmin || (isAuthor && canUpdate) ? (
                  <ControlsActions
                    style={{ marginTop: '-10px', marginRight: '-10px' }}
                    actions={[
                      {
                        icon: <EditOutlined />,
                        onClick: () => redirectToNewsEdit(item.id),
                      },
                    ]}
                  />
                ) : null}
              </Flex>
            </div>
            <div
              className={`xs-mb-10 content-wrapper add-icons-to-file-anchors`}
            >
              {item.image_name && (
                <div className="foto-wrapper">
                  <img
                    src={'/storage/presentation_images/' + item?.image_name}
                    alt={title}
                    title={title}
                    onError={({ currentTarget }) => {
                      if (
                        getFilenameFromPath(currentTarget.src) !==
                        getFilenameFromPath(DEFAULT_IMAGE)
                      ) {
                        // prevents looping
                        currentTarget.onerror = null;
                        currentTarget.src = DEFAULT_IMAGE;
                      }
                    }}
                  />
                </div>
              )}

              <RawHtml classes="text-wrapper" stripImages stripLinks>
                {content}
              </RawHtml>
            </div>
          </div>
          <Flex justify={'space-between'} align={'flex-end'}>
            <NewsSignature
              nameOfCreator={formattedFullName(item.person)}
              dateOfCreation={dayjs(item.created_at || new Date()).format(
                DATETIME_FORMAT_DATETIME,
              )}
            />
          </Flex>
        </div>

        <NewsModal
          toggleModal={() => displayModal(false)}
          title={title}
          content={content}
          presentationImage={item.image_name || DEFAULT_IMAGE}
          creatorName={(item.person && formattedFullName(item.person)) || ''}
          dateOfCreate={dayjs(item.created_at || new Date()).format(
            DATETIME_FORMAT_DATETIME,
          )}
          visible={modalVisible}
        />
      </Badge.Ribbon>
    </Col>
  );
};

NewsItem.propTypes = {
  item: PropTypes.object.isRequired,
  redirectToNewsEdit: PropTypes.func,
};

export default NewsItem;
